export const HERO_SLIDES = [
  {
    id: "diyer",
    label: "DIYER",
    headline: "Get on-demand tradie advice!",
    description:
      "Access live video advice instantly from trusted and qualified tradespeople on your mobile phone, tablet, or desktop, no matter where you are.",
    image: "/images/homev2/hero1_diyer.png",
    imageAlt: "DIYers ready to paint with rollers",
    imageClassName:
      "translate-y-[35%] object-contain object-bottom drop-shadow-2xl lg:origin-bottom lg:scale-[1.35]",
    primaryCta: { label: "Find a tradie", href: "/v2/login" },
    secondaryCta: {
      label: "Join us now, free!",
      href: "/v2/login?mode=sign-up",
    },
  },
  {
    id: "tradie",
    label: "TRADIE",
    headline: "Grow your business with quality jobs!",
    description:
      "Connect with homeowners looking for your expertise. Get matched with jobs that fit your skills, set your own rates, and build your reputation through quality work.",
    image: "/images/homev2/hero2_tradie.png",
    imageAlt: "Tradie guiding a DIYer at a tile saw",
    // Independent from slide 1 — pushed up 50% vs diyer’s +35%
    imageClassName:
      "translate-y-[-5%] translate-x-[15%] object-contain object-bottom drop-shadow-2xl lg:origin-bottom lg:scale-[1.25]",
    primaryCta: { label: "Browse jobs", href: "/v2/login" },
    secondaryCta: {
      label: "Join as a tradie, free!",
      href: "#how-it-works",
    },
  },
] as const;

export const LOGIN_SLIDES = [
  {
    id: "guidance",
    image: "/images/homev2/tradie_painter.jpg",
    imageAlt: "DIYers painting together",
    headline: "Simple Sign-Up for Expert Guidance",
    description:
      "Easy Sign-Up for Instant Access to a Network of Qualified Tradies - Your DIY Helper is Just a Few Clicks Away!",
  },
  {
    id: "tradie-work",
    image: "/images/homev2/tradie_tiler.jpg",
    imageAlt: "Tradie at work on site",
    headline: "Grow Your Trade. Help DIYers.",
    description:
      "Connect with homeowners who need expert guidance. Take focused video calls and earn on your schedule.",
  },
  {
    id: "quality",
    image: "/images/homev2/tradie_gardener.jpg",
    imageAlt: "Professional tradie outdoors",
    headline: "Trusted Pros. Real Advice.",
    description:
      "Get live video advice from verified tradespeople — on your phone, tablet, or desktop, wherever you are.",
  },
] as const;

export const SESSION_FEATURES = [
  "Verified professionals",
  "Project guidance & recommendations",
  "15-minute live consultation",
  "Transparent session tracking",
  "In-platform chat & file sharing",
] as const;

export const MONTHLY_FEATURES = [
  "Unlimited short sessions*",
  "Priority matching with tradies",
  "Saved project history",
  "In-platform chat & file sharing",
  "Cancel anytime",
] as const;

export const FEATURE_CARDS = [
  {
    id: "qualified",
    title: "Qualified and licensed tradespeople",
    description:
      "Get advice from verified professionals with real-world experience across a wide range of trades.",
    image: "/images/homev2/feature_realtradepeople_1.png",
    imageAlt: "Tradie profile cards",
    tall: true,
    // Independent placement — tune this card only
    imagePlacement: {
      areaClassName: "absolute inset-x-0 top-[22%] bottom-0 overflow-hidden",
      imageClassName:
        "object-contain object-top origin-top translate-x-[10px] translate-y-[56px] scale-[0.97]",
      spacerClassName: "min-h-[280px] flex-1",
    },
  },
  {
    id: "help",
    title: "Get help as you work",
    description:
      "Access professional guidance from your phone, tablet, or desktop—no matter where you are.",
    image: "/images/homev2/feature_realtradepeople_2.png",
    imageAlt: "Live video call with a tradie",
    tall: false,
    imagePlacement: {
      areaClassName:
        "absolute inset-x-0 bottom-0 h-[52%] overflow-hidden left-[7%] ",
      imageClassName:
        "object-contain object-bottom translate-y-[6px] origin-bottom scale-[1.14]",
      spacerClassName: "min-h-[160px]",
    },
  },
  {
    id: "projects",
    title: "Built for real project needs",
    description:
      "From quick fixes to complex builds, get practical advice tailored to your specific situation.",
    image: "/images/homev2/feature_realtradepeople_4.png",
    imageAlt: "Secure payment and project tools illustration",
    tall: false,
    imagePlacement: {
      areaClassName: "absolute inset-x-0 bottom-0 h-[52%] overflow-hidden",
      imageClassName:
        "object-contain object-bottom origin-bottom scale-[1.4] translate-y-[80px]",
      spacerClassName: "min-h-[160px]",
    },
  },
  {
    id: "secure",
    title: "Secure personal and payment data",
    description:
      "Your information is protected with trusted, industry-standard security powered by providers like AWS and Stripe.",
    image: "/images/homev2/feature_realtradepeople_3.png",
    imageAlt: "Construction tools and blueprints",
    tall: true,
    imagePlacement: {
      areaClassName: "absolute inset-x-0 top-[27%] bottom-0 overflow-hidden",
      imageClassName:
        "object-contain object-top origin-top translate-x-[25px] translate-y-[80px] scale-[1]",
      spacerClassName: "min-h-[280px] flex-1",
    },
  },
] as const;

export const TESTIMONIALS = [
  {
    id: "daniel",
    quote:
      "Teletradie linked me up with Sarah, and honestly, that saved me hours of guessing. She spotted things I completely missed and walked me through fixes step by step. It felt less like hiring help and more like having a pro guide me through the whole process.",
    name: "Daniel Foster",
    role: "DIY Homeowner",
    avatar: "/images/team/michael-foster.jpg",
  },
  {
    id: "chloe",
    quote:
      "I wasn't sure if a quick call would actually help, but it did. Sarah broke everything down in a way that made sense, and I actually felt confident doing the work myself after. The whole experience was smooth, fast, and way more useful than I expected.",
    name: "Chloe Martinez",
    role: "First-Time Renovator",
    avatar: "/images/team/chelsea-hagon.jpg",
  },
  {
    id: "ryan",
    quote:
      "I've worked with plenty of platforms, but Teletradie feels different. Clients come in already serious, and the calls stay focused. It's efficient on my end, and I can genuinely help people solve real problems without wasting time going back and forth.",
    name: "Ryan Patel",
    role: "Licensed Electrician",
    avatar: "/images/team/jeffrey-webb.jpg",
  },
  {
    id: "marcus",
    quote:
      "I was halfway through a tiling job and completely stuck on the layout. Jumped on Teletradie and got real advice in minutes. No fluff, just clear direction that actually worked. Saved me from redoing the whole thing and wasting more time.",
    name: "Marcus Lee",
    role: "DIY Homeowner",
    avatar: "/images/team/blake-reid.jpg",
  },
] as const;

export const EXPERT_CATEGORIES = [
  {
    id: "carpentry",
    label: "Carpentry",
    title: "Carpentry",
    description:
      "Get professional carpentry advice online for builds, repairs, and custom wood projects. From shelving installation to deck construction, connect with experienced carpenters for real-time guidance.",
    features: [
      "Custom creations & installations",
      "Precision woodwork techniques",
      "Restoration & refinishing support",
    ],
    image: "/images/homev2/tradie_painter.jpg",
    imageAlt: "Carpenter using a power tool",
  },
  {
    id: "tiling",
    label: "Tiling",
    title: "Tiling",
    description:
      "Get expert tiling guidance for bathrooms, kitchens, and floors. Learn layout, cutting, and finishing techniques from experienced tilers.",
    features: [
      "Layout & pattern planning",
      "Cutting & waterproofing tips",
      "Grouting & finishing support",
    ],
    image: "/images/homev2/tradie_tiler.jpg",
    imageAlt: "Tiler at work",
  },
  {
    id: "painting",
    label: "Painting",
    title: "Painting",
    description:
      "Prep, colour matching, and clean finishes—get live advice from painters for interiors, exteriors, and touch-ups.",
    features: [
      "Surface prep & priming",
      "Colour & finish guidance",
      "Trim and detail techniques",
    ],
    image: "/images/homev2/tradie_painter.jpg",
    imageAlt: "Painter at work",
  },
  {
    id: "gardening",
    label: "Gardening",
    title: "Gardening",
    description:
      "From lawn care to landscaping layouts, connect with gardeners who can guide your outdoor projects step by step.",
    features: [
      "Plant selection & care",
      "Garden layout advice",
      "Seasonal maintenance tips",
    ],
    image: "/images/homev2/tradie_gardener.jpg",
    imageAlt: "Gardener outdoors",
  },
  {
    id: "plumbing",
    label: "Plumbing",
    title: "Plumbing",
    description:
      "Troubleshoot leaks, fixtures, and installs with licensed plumbers—safe guidance before you call for a site visit.",
    features: [
      "Fixture troubleshooting",
      "Install planning support",
      "Safety & compliance tips",
    ],
    image: "/images/homev2/tradie_plummer.jpg",
    imageAlt: "Plumber at work",
  },
  {
    id: "interior",
    label: "Interior Design",
    title: "Interior Design",
    description:
      "Space planning, material choices, and styling advice from interior designers—tailored to your home and budget.",
    features: [
      "Space planning ideas",
      "Material & colour guidance",
      "Furniture layout support",
    ],
    image: "/images/homev2/tradie_designer.png",
    imageAlt: "Interior designer",
  },
] as const;

export const TRADIES = [
  {
    id: "rodrigo",
    title: "Gardening Tips",
    name: "Rodrigo",
    image: "/images/homev2/tradie_gardener.jpg",
  },
  {
    id: "lucy",
    title: "Interior Design",
    name: "Lucy",
    image: "/images/homev2/tradie_designer.png",
  },
  {
    id: "alex",
    title: "Building Tips",
    name: "Alex",
    image: "/images/homev2/tradie_painter.jpg",
  },
  {
    id: "sam",
    title: "Tiling Tips",
    name: "Sam",
    image: "/images/homev2/tradie_tiler.jpg",
  },
  {
    id: "jordan",
    title: "Plumbing Tips",
    name: "Jordan",
    image: "/images/homev2/tradie_plummer.jpg",
  },
] as const;

export const HOW_IT_WORKS = [
  {
    id: "register",
    title: "Log in or register",
    description:
      "Easily log in or create your TeleTradie account in just a few simple steps to gain access to our network of qualified tradies",
    image: "/images/homev2/how-it-works-1.png",
    imageAlt: "TeleTradie sign up screen",
  },
  {
    id: "trade",
    title: "Choose your trade",
    description: "Choose the trade that best suits your DIY project",
    image: "/images/homev2/how-it-works-2.png",
    imageAlt: "Choose a trade screen",
  },
  {
    id: "call",
    title: "Request a call",
    description:
      "Request a video call with one of our qualified tradespeople, who are ready to assist you with your DIY project",
    image: "/images/homev2/how-it-works-3.png",
    imageAlt: "Live video call with a tradie",
  },
] as const;
